import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Menu, Briefcase, Heart } from "lucide-react";
import { useGlassShine } from "@/hooks/useGlassShine";
import { Button } from "@/components/ui/button";
import { APP_NAME } from "@/lib/constants";
import logo from "@/assets/business-pal-logo.png";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const industries = [
  { name: "Beauty & Hair", slug: "beauty", icon: "✂️" },
  { name: "Healthcare", slug: "healthcare", icon: "🏥" },
  { name: "Wellbeing & Spa", slug: "wellbeing", icon: "🧘" },
  { name: "Education & Tutoring", slug: "education", icon: "📚" },
  { name: "Professional Services", slug: "professional-services", icon: "💼" },
  { name: "Trade Services", slug: "trade", icon: "🔧" },
  { name: "Creative Services", slug: "creative", icon: "🎨" },
];

const products = [
  { name: "AI HR & Recruitment", slug: "/ai-hr", icon: Briefcase },
  { name: "AI Greeting Cards", slug: "/greeting-cards", icon: Heart },
];

export function LandingHeader() {
  useGlassShine();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 h-16">
      <div className="container flex items-center justify-between px-4 h-full">
        <Link to="/" className="flex items-center gap-3">
          <img src={logo} alt={APP_NAME} className="h-10 w-auto object-contain" width="40" height="40" />
          <span className="font-bold text-xl hidden sm:block">{APP_NAME}</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6">
          <Link to="/features" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            Features
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              Products
              <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="w-56">
              {products.map((product) => (
                <DropdownMenuItem key={product.slug} asChild>
                  <Link to={product.slug} className="flex items-center gap-2">
                    <product.icon className="h-4 w-4 text-primary" />
                    <span>{product.name}</span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              Industries
              <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="w-56">
              {industries.map((industry) => (
                <DropdownMenuItem key={industry.slug} asChild>
                  <Link to={`/industries/${industry.slug}`} className="flex items-center gap-2">
                    <span>{industry.icon}</span>
                    <span>{industry.name}</span>
                  </Link>
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem asChild>
                <Link to="/industries" className="flex items-center gap-2 font-medium text-primary">
                  View All Industries
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Link to="/pricing" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            Pricing
          </Link>
          <Link to="/blog" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            Blog
          </Link>
          <Link to="/contact" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            Contact
          </Link>
        </nav>

        {/* Desktop buttons + Mobile hamburger */}
        <div className="flex items-center gap-3">
          <div className="hidden lg:flex items-center gap-3">
            <Button variant="ghost" asChild>
              <Link to="/login">Login</Link>
            </Button>
            <Button asChild className="bg-primary hover:bg-primary/90">
              <Link to="/signup/business">Start Now</Link>
            </Button>
          </div>

          {/* Mobile menu trigger */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 flex flex-col">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>

              <nav className="flex flex-col gap-1 mt-4 flex-1">
                <Link
                  to="/features"
                  onClick={() => setOpen(false)}
                  className="px-3 py-2.5 text-sm font-medium rounded-md hover:bg-accent hover:text-white transition-colors"
                >
                  Features
                </Link>

                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="products" className="border-none">
                    <AccordionTrigger className="px-3 py-2.5 text-sm font-medium hover:no-underline hover:bg-accent hover:text-white rounded-md">
                      Products
                    </AccordionTrigger>
                    <AccordionContent className="pb-0">
                      <div className="flex flex-col gap-0.5 pl-4">
                        {products.map((product) => (
                          <Link
                            key={product.slug}
                            to={product.slug}
                            onClick={() => setOpen(false)}
                            className="flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-accent hover:text-white transition-colors"
                          >
                            <product.icon className="h-4 w-4 text-primary" />
                            <span>{product.name}</span>
                          </Link>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="industries" className="border-none">
                    <AccordionTrigger className="px-3 py-2.5 text-sm font-medium hover:no-underline hover:bg-accent hover:text-white rounded-md">
                      Industries
                    </AccordionTrigger>
                    <AccordionContent className="pb-0">
                      <div className="flex flex-col gap-0.5 pl-4">
                        {industries.map((industry) => (
                          <Link
                            key={industry.slug}
                            to={`/industries/${industry.slug}`}
                            onClick={() => setOpen(false)}
                            className="flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-accent hover:text-white transition-colors"
                          >
                            <span>{industry.icon}</span>
                            <span>{industry.name}</span>
                          </Link>
                        ))}
                        <Link
                          to="/industries"
                          onClick={() => setOpen(false)}
                          className="px-3 py-2 text-sm font-medium text-primary rounded-md hover:bg-accent hover:text-white transition-colors"
                        >
                          View All Industries
                        </Link>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                <Link
                  to="/pricing"
                  onClick={() => setOpen(false)}
                  className="px-3 py-2.5 text-sm font-medium rounded-md hover:bg-accent hover:text-white transition-colors"
                >
                  Pricing
                </Link>
                <Link
                  to="/blog"
                  onClick={() => setOpen(false)}
                  className="px-3 py-2.5 text-sm font-medium rounded-md hover:bg-accent hover:text-white transition-colors"
                >
                  Blog
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setOpen(false)}
                  className="px-3 py-2.5 text-sm font-medium rounded-md hover:bg-accent hover:text-white transition-colors"
                >
                  Contact
                </Link>
              </nav>

              <div className="flex flex-col gap-2 pt-4 border-t">
                <Button variant="outline" asChild className="w-full" onClick={() => setOpen(false)}>
                  <Link to="/login">Login</Link>
                </Button>
                <Button asChild className="w-full bg-primary hover:bg-primary/90" onClick={() => setOpen(false)}>
                  <Link to="/signup/business">Start Now</Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
